/**
 * FmCardGrid — responsive auto-fit grid of standalone object cards.
 *
 * Owns only rhythm and column collapse; each child is a solid FmCard (e.g. the
 * My Forests property card grid). Replaces page-local
 * `display:grid; repeat(auto-fill, …)` CSS so every card grid shares one
 * collapse contract and stays gate-clean. The grid auto-fills as many equal
 * columns as fit and collapses to a single column on narrow viewports.
 */
import React from 'react';
import './FmLayout.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export type FmCardGridMinColumn = 'sm' | 'md' | 'lg';

export interface FmCardGridProps {
  /** Minimum column width band. `'md'` (default) suits property-home cards. */
  minColumn?: FmCardGridMinColumn;
  as?: 'div' | 'section';
  role?: React.AriaRole;
  children: React.ReactNode;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

export const FmCardGrid: React.FC<FmCardGridProps> = ({
  minColumn = 'md',
  as: Element = 'div',
  role,
  children,
  className,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => (
  <Element
    className={cx('fm-card-grid', `fm-card-grid--${minColumn}`, className)}
    role={role}
    aria-label={ariaLabel}
    data-testid={dataTestId}
  >
    {children}
  </Element>
);

export default FmCardGrid;
